import { logError } from "@/logging/logging";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  selectIsThemeModeLoaded,
  selectThemeMode,
  setThemeMode,
} from "@/store/theme/theme-slice";
import { FaroToggleGroup, ToggleItem, useToast } from "@faro-lotv/flat-ui";
import { useAuthContext } from "@faro-lotv/gate-keepers";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { SettingsApiTypes } from "@stellar/settings-api-client";
import { useState, type JSX } from "react";

const THEME_MODE_ITEMS: Array<
  ToggleItem<SettingsApiTypes.SupportedThemeModes>
> = [
  { label: "Light", value: "light" },
  { label: "Dark", value: "dark" },
  { label: "System", value: "system" },
];

/** @returns a toggle to change the `theme_mode` user setting, which is shared with the Dashboard */
export function ThemeModeToggle(): JSX.Element {
  const dispatch = useAppDispatch();
  const { settingsApiClient } = useApiClientContext();
  const { openToast } = useToast();
  const { isLoggedIn } = useAuthContext();
  const themeMode = useAppSelector(selectThemeMode);
  const isLoaded = useAppSelector(selectIsThemeModeLoaded);
  const [isSaving, setIsSaving] = useState(false);

  async function changeThemeMode(
    newThemeMode: SettingsApiTypes.SupportedThemeModes,
  ): Promise<void> {
    dispatch(setThemeMode(newThemeMode));
    setIsSaving(true);
    try {
      const { modifiedAt } =
        await settingsApiClient.user.fetchUserSettings("theme_mode");
      await settingsApiClient.user.updateUserSettings("theme_mode", {
        value: newThemeMode,
        modifiedAt,
      });
    } catch (error) {
      dispatch(setThemeMode(themeMode));
      logError(error);
      openToast({ title: "Failed to save the theme mode.", variant: "error" });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <FaroToggleGroup
      items={THEME_MODE_ITEMS}
      value={themeMode}
      onChange={changeThemeMode}
      // The setting is per user, so it can't be saved without one.
      // Wait for the initial load, otherwise its response would overwrite the user's choice.
      isDisabled={isSaving || !isLoggedIn || !isLoaded}
    />
  );
}
